import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { DndContext, DragEndEvent, DragOverlay, DragStartEvent, closestCorners, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, horizontalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { boardsAPI, Board, Column, Task } from '../api/api';
import { tasksAPI } from '../api/api';
import { useAuth } from '../context/AuthContext';
import Navbar from './Navbar';
import SortableColumn from './SortableColumn';
import CreateTaskModal from './CreateTaskModal';
import CreateColumnModal from './CreateColumnModal';
import BoardMembersModal from './BoardMembersModal';
import EditBoardModal from './EditBoardModal';
import EditColumnModal from './EditColumnModal';
import './BoardView.css';

const BoardView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, isAdmin, isManager } = useAuth();
  const [board, setBoard] = useState<Board | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTask, setActiveTask] = useState<Task | null>(null);
  const [activeColumn, setActiveColumn] = useState<Column | null>(null);
  const [showCreateTask, setShowCreateTask] = useState(false);
  const [showCreateColumn, setShowCreateColumn] = useState(false);
  const [showEditColumn, setShowEditColumn] = useState(false);
  const [editingColumn, setEditingColumn] = useState<Column | null>(null);
  const [showMembersModal, setShowMembersModal] = useState(false);
  const [showEditBoard, setShowEditBoard] = useState(false);
  const [selectedColumnId, setSelectedColumnId] = useState<number | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  const loadBoard = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      const data = await boardsAPI.getById(parseInt(id));
      setBoard(data);
    } catch (error: any) {
      console.error('Ошибка при загрузке доски:', error);
      if (error.response?.status === 403) {
        setBoard(null); // Clear board to show error message
      }
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      loadBoard();
    }
  }, [id, loadBoard]);

  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;
    const activeData = active.data.current;
    
    // Проверяем, перетаскивается ли столбец
    if (activeData?.type === 'column') {
      setActiveColumn(activeData.column);
      return;
    }
    
    // Иначе перетаскивается задача
    const activeIdStr = active.id as string;
    if (activeIdStr.startsWith('task-') && board?.columns) {
      const taskId = parseInt(activeIdStr.replace('task-', ''), 10);
      for (const column of board.columns) {
        const task = column.tasks?.find((t) => t.id === taskId);
        if (task) {
          setActiveTask(task);
          break;
        }
      }
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveTask(null);
    setActiveColumn(null);

    if (!over || !board) return;

    const activeData = active.data.current;
    
    // Обрабатываем перетаскивание столбцов
    if (activeData?.type === 'column') {
      const activeIdStr = active.id as string;
      const columnId = activeIdStr.startsWith('column-') ? parseInt(activeIdStr.replace('column-', ''), 10) : parseInt(activeIdStr, 10);
      const overId = over.id as string;
      const overData = over.data.current;
      
      // Определяем целевой столбец
      let targetColumnId: number | null = null;
      
      // Если перетаскивание на другой столбец напрямую
      if (overData?.type === 'column' && overId.startsWith('column-')) {
        targetColumnId = parseInt(overId.replace('column-', ''), 10);
      } else if (overId.startsWith('column-')) {
        targetColumnId = parseInt(overId.replace('column-', ''), 10);
      } else if (overId.startsWith('task-')) {
        // Перетаскивание на задачу — находим столбец этой задачи
        const overTaskId = parseInt(overId.replace('task-', ''), 10);
        for (const column of board.columns || []) {
          const task = column.tasks?.find((t) => t.id === overTaskId);
          if (task) {
            targetColumnId = column.id;
            break;
          }
        }
      }
      
      if (targetColumnId && targetColumnId !== columnId) {
        const columns = board.columns || [];
        const oldIndex = columns.findIndex((col) => col.id === columnId);
        const newIndex = columns.findIndex((col) => col.id === targetColumnId);
        
        if (oldIndex !== -1 && newIndex !== -1 && oldIndex !== newIndex) {
          // Обновляем порядок столбцов
          const newColumns = arrayMove(columns, oldIndex, newIndex);
          setBoard({ ...board, columns: newColumns });
          
          // Обновляем позиции на сервере
          try {
            const updatePromises = newColumns.map((col, index) =>
              boardsAPI.updateColumn(board.id, col.id, { position: index })
            );
            await Promise.all(updatePromises);
          } catch (error) {
            console.error('Ошибка при обновлении позиций столбцов:', error);
            await loadBoard();
          }
        }
      }
      return;
    }

    // Обрабатываем перетаскивание задач
    const activeIdStr = active.id as string;
    if (!activeIdStr.startsWith('task-')) return;
    const taskId = parseInt(activeIdStr.replace('task-', ''), 10);
    
    // Find target column
    let targetColumnId: number | null = null;
    let targetPosition = 0;
    const overId = over.id as string;

    // Приоритет 1: Check if dropped directly on a column (via droppable data)
    if (over.data.current?.columnId) {
      targetColumnId = parseInt(over.data.current.columnId);
      const targetColumn = board.columns?.find((col) => col.id === targetColumnId);
      targetPosition = targetColumn?.tasks?.length || 0;
    } 
    // Приоритет 2: Check if dropped on column itself (by id with prefix)
    else if (overId.startsWith('column-')) {
      targetColumnId = parseInt(overId.replace('column-', ''), 10);
      const columnMatch = board.columns?.find((col) => col.id === targetColumnId);
      targetPosition = columnMatch?.tasks?.length || 0;
    }
    // Приоритет 3: Check if dropped on a task - find its column
    else if (overId.startsWith('task-')) {
      const overTaskId = parseInt(overId.replace('task-', ''), 10);
      for (const column of board.columns || []) {
        const task = column.tasks?.find((t) => t.id === overTaskId);
        if (task) {
          targetColumnId = column.id;
          const taskIndex = column.tasks!.indexOf(task);
          targetPosition = taskIndex;
          break;
        }
      }
    }

    if (!targetColumnId) {
      console.warn('Could not determine target column for drop:', {
        overId: over.id,
        overData: over.data.current
      });
      return;
    }

    // Find current column and task
    let currentColumn: Column | undefined;
    let task: Task | undefined;

    for (const column of board.columns || []) {
      task = column.tasks?.find((t) => t.id === taskId);
      if (task) {
        currentColumn = column;
        break;
      }
    }

    if (!task || !currentColumn) return;

    // If dropped in the same column, calculate new position
    if (currentColumn.id === targetColumnId) {
      const tasks = currentColumn.tasks || [];
      const oldIndex = tasks.findIndex((t) => t.id === taskId);
      
      // If dropped on another task, position after it
      const overIdStr = over.id as string;
      const overTaskId = overIdStr.startsWith('task-') ? parseInt(overIdStr.replace('task-', ''), 10) : null;
      const targetTask = overTaskId != null ? tasks.find((t) => t.id === overTaskId) : undefined;
      if (targetTask) {
        targetPosition = tasks.indexOf(targetTask);
        if (oldIndex < targetPosition) targetPosition++;
      } else {
        targetPosition = oldIndex; // Stay in place
      }

      if (oldIndex !== targetPosition && targetPosition >= 0) {
        const newTasks = arrayMove(tasks, oldIndex, targetPosition);
        const updatedColumns = board.columns?.map((col) =>
          col.id === currentColumn!.id
            ? { ...col, tasks: newTasks.map((t: Task, idx: number) => ({ ...t, position: idx })) }
            : col
        );

        setBoard({ ...board, columns: updatedColumns });

        try {
          await tasksAPI.move(taskId, targetColumnId, targetPosition);
        } catch (error) {
          console.error('Ошибка при перемещении задачи:', error);
          await loadBoard();
        }
      }
    } else {
      // Move to different column
      if (!currentColumn || !task) return;
      
      const targetColumn = board.columns?.find((col) => col.id === targetColumnId);
      if (!targetColumn) return;
      
      // Определяем финальную позицию
      let finalPosition = targetColumn.tasks?.length || 0;
      
      // Если drop был на задачу (а не на столбец), размещаем после этой задачи
      const overTaskId = overId.startsWith('task-') ? parseInt(overId.replace('task-', ''), 10) : null;
      const droppedOnTask = overTaskId != null && targetColumn.tasks?.some((t) => t.id === overTaskId);
      if (droppedOnTask && overTaskId != null) {
        // Находим задачу, на которую был drop
        const targetTaskIndex = targetColumn.tasks!.findIndex((t) => t.id === overTaskId);
        if (targetTaskIndex !== -1) {
          // Размещаем после задачи, на которую был drop
          finalPosition = targetTaskIndex + 1;
        }
      }
      
      // Optimistically update UI
      const updatedColumns = board.columns?.map((col) => {
        if (col.id === currentColumn!.id) {
          // Remove task from current column
          return {
            ...col,
            tasks: (col.tasks || []).filter((t) => t.id !== taskId)
          };
        } else if (col.id === targetColumnId) {
          // Add task to target column
          // task is guaranteed to be defined here due to check above
          const newTasks = [...(col.tasks || [])];
          const updatedTask = {
            ...task!,
            column_id: targetColumnId,
            position: finalPosition
          } as Task;
          newTasks.splice(finalPosition, 0, updatedTask);
          return {
            ...col,
            tasks: newTasks.map((t: Task, idx: number) => ({ ...t, position: idx }))
          };
        }
        return col;
      });

      setBoard({ ...board, columns: updatedColumns });

      // Update on server
      try {
        await tasksAPI.move(taskId, targetColumnId, finalPosition);
        // UI already updated optimistically, no need to reload
      } catch (error) {
        console.error('Ошибка при перемещении задачи:', error);
        // Revert on error - reload to get correct state
        await loadBoard();
      }
    }
  };

  const handleTaskCreated = () => {
    loadBoard();
    setShowCreateTask(false);
  };

  const handleColumnCreated = () => {
    loadBoard();
    setShowCreateColumn(false);
  };

  const handleEditColumn = (column: Column) => {
    setEditingColumn(column);
    setShowEditColumn(true);
  };

  const handleColumnUpdated = () => {
    loadBoard();
    setShowEditColumn(false);
    setEditingColumn(null);
  };

  const handleDeleteColumn = async (columnId: number) => {
    if (!board || !window.confirm('Удалить этот столбец? Все задачи будут удалены.')) {
      return;
    }

    try {
      await boardsAPI.deleteColumn(board.id, columnId);
      await loadBoard();
    } catch (error) {
      console.error('Ошибка при удалении столбца:', error);
    }
  };

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="loading">Загрузка...</div>
      </>
    );
  }

  if (!board) {
    return (
      <>
        <Navbar />
        <div className="error-message">
          <h2>Доска не найдена или нет доступа</h2>
          <p>Эта доска может быть приватной, и у вас нет прав для её просмотра.</p>
          <button onClick={() => navigate('/boards')} className="back-btn" style={{ marginTop: '20px' }}>
            ← Вернуться к доскам
          </button>
        </div>
      </>
    );
  }

  return (
    <>
      <Navbar />
      <div className="board-view-container">
        <div className="board-view-header">
          <button onClick={() => navigate('/boards')} className="back-btn">
            <span className="back-arrow">←</span> Назад к доскам
          </button>
          <div className="board-title-section">
          </div>
          <div className="board-header-actions">
            {board.created_by === user?.id && (
              <button
                onClick={() => setShowEditBoard(true)}
                className="edit-board-btn"
                title="Редактировать доску"
              >
                ✏️ Редактировать
              </button>
            )}
            {board.visibility === 'private' && board.created_by === user?.id && (
              <button
                onClick={() => setShowMembersModal(true)}
                className="members-btn"
                title="Управление участниками"
              >
                <span>Участники</span>
              </button>
            )}
            {(isAdmin || isManager) && (
            <button
              onClick={() => setShowCreateColumn(true)}
              className="add-column-btn"
            >
              + Добавить столбец
            </button>
            )}
          </div>
        </div>
     {/* вот сюда под описание */}
        <div className="board-title-row">
          <h1 className='board-title-h1' >{board.name}</h1>
         <div className='board-bottom'>
         <span className={`board-visibility-badge ${board.visibility === 'private' ? 'private' : 'public'}`}>
            {board.visibility === 'private' ? '🔒 Приватный' : '🌐 Общий'}
          </span>
          {board.visibility === 'private' && board.members && (
            <span className="members-count">👥 {board.members.length + 1} участник{board.members.length === 0 ? '' : board.members.length === 1 ? '' : 'а'}</span>
          )}
         </div>
        </div>
        {board.description && (
          <p className="board-description">{board.description}</p>
        )}

        <DndContext
          sensors={sensors}
          collisionDetection={closestCorners}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          autoScroll={false}
        >
          <SortableContext
            items={(board.columns || []).map((col) => `column-${col.id}`)}
            strategy={horizontalListSortingStrategy}
          >
            <div className="board-columns">
              {board.columns?.map((column) => (
                <SortableContext
                  key={column.id}
                  id={`column-${column.id}`}
                  items={(column.tasks || []).map((t) => `task-${t.id}`)}
                  strategy={verticalListSortingStrategy}
                >
                  <SortableColumn
                    column={column}
                    onAddTask={() => {
                      setSelectedColumnId(column.id);
                      setShowCreateTask(true);
                    }}
                    onDeleteColumn={() => handleDeleteColumn(column.id)}
                    onEditColumn={() => handleEditColumn(column)}
                    onTaskUpdate={loadBoard}
                    canDelete={isAdmin || isManager}
                  />
                </SortableContext>
              ))}
            </div>
          </SortableContext>
          <DragOverlay>
            {activeColumn ? (
              <div className="column-dragging" style={{ width: '300px' }}>
                <div className="board-column">
                  <div className="column-header">
                    <h3>{activeColumn.name}</h3>
                  </div>
                </div>
              </div>
            ) : activeTask ? (
              <div className="task-card-dragging">
                <div className="task-card-title">{activeTask.title}</div>
                {activeTask.assigned_to_username && (
                  <div className="task-card-assigned">
                    Исполнитель: {activeTask.assigned_to_username}
                  </div>
                )}
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>

        {showCreateTask && selectedColumnId && (
          <CreateTaskModal
            boardId={board.id}
            columnId={selectedColumnId}
            onClose={() => {
              setShowCreateTask(false);
              setSelectedColumnId(null);
            }}
            onTaskCreated={handleTaskCreated}
          />
        )}

        {showCreateColumn && (
          <CreateColumnModal
            boardId={board.id}
            onClose={() => setShowCreateColumn(false)}
            onColumnCreated={handleColumnCreated}
          />
        )}

        {showEditColumn && editingColumn && board && (
          <EditColumnModal
            boardId={board.id}
            columnId={editingColumn.id}
            currentName={editingColumn.name}
            onClose={() => {
              setShowEditColumn(false);
              setEditingColumn(null);
            }}
            onColumnUpdated={handleColumnUpdated}
          />
        )}

        {showMembersModal && board && (
          <BoardMembersModal
            board={board}
            onClose={() => setShowMembersModal(false)}
            onMembersUpdated={() => {
              loadBoard();
            }}
          />
        )}

        {showEditBoard && board && (
          <EditBoardModal
            board={board}
            onClose={() => setShowEditBoard(false)}
            onBoardUpdated={() => {
              loadBoard();
              setShowEditBoard(false);
            }}
          />
        )}
      </div>
    </>
  );
};

export default BoardView;

